import { useEffect, useState } from 'react';
import { DateRange } from 'react-day-picker';
import { type ClassValue, clsx } from 'clsx';
import { endOfDay, format, isSameDay, startOfDay } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { twMerge } from 'tailwind-merge';
import { FileUploadValue, MultipleFileUploadValue } from './types/file';

export const REQUIRED_FIELD_SYMBOL = '*';

export const DATE_TIME_BACKEND_FORMAT = "yyyy-MM-dd'T'HH:mm:ss'Z'";

export const DATE_TIME_BACKEND_FORMAT_NO_ZONE = "yyyy-MM-dd'T'HH:mm:ss";

export const MAX_DATE_FILTER_YEARS = 5;

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const useDebouncedValue = <T>(inputValue: T, delay: number = 500) => {
  const [debouncedValue, setDebouncedValue] = useState(inputValue);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(inputValue);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [inputValue, delay]);

  return debouncedValue;
};

export const getBackendTimestamp = (
  date: Date | string,
  formatTypeString: string = DATE_TIME_BACKEND_FORMAT
) => format(toZonedTime(date, 'utc'), formatTypeString);

export const getDayDateRange = (date: Date) => {
  const start = startOfDay(date);
  const end = endOfDay(date);

  return [start, end] as const;
};

export const extractFile = (value: FileUploadValue | null): File | null =>
  value?.file ?? null;

export const extractFiles = (value: MultipleFileUploadValue | null): File[] =>
  value?.files ?? [];

export const getTemplateUrl = (
  value: FileUploadValue | MultipleFileUploadValue | null | undefined
): string | null => {
  if (!value) return null;

  return value.templateMetadata?.url ?? null;
};

export const normalizeFormArray = <T>(items?: T[]) => {
  if (!items || items.length === 0) return undefined;

  return items;
};

export const formatRangeLabel = (
  range: DateRange | undefined,
  dateFormat: string,
  singleDateFormat?: string
) => {
  if (!range?.from) return null;

  if (!range.to) {
    return singleDateFormat
      ? format(range.from, singleDateFormat)
      : format(range.from, dateFormat);
  }

  if (isSameDay(range.from, range.to) && singleDateFormat)
    return format(range.from, singleDateFormat);

  const from = format(range.from, dateFormat);
  const to = format(range.to, dateFormat);

  return `${from} – ${to}`;
};

export const parseFilterDate = (value?: string | null): Date | null => {
  if (!value) return null;
  const dateString = value.endsWith('Z') ? value : `${value}Z`;
  const date = new Date(dateString);

  return isNaN(date.getTime()) ? null : date;
};

export const toBackendDayRange = (from: Date, to?: Date): [string, string] => {
  const [start] = getDayDateRange(from);
  const [, end] = getDayDateRange(to ?? from);

  return [
    getBackendTimestamp(start, DATE_TIME_BACKEND_FORMAT_NO_ZONE),
    getBackendTimestamp(end, DATE_TIME_BACKEND_FORMAT_NO_ZONE),
  ];
};
